/** Oʻyindan oldin soʻz tanlash (demo `PK`/`openPicker`/`launchGame`): tasodifiy yoki oʻzim tanlagan soʻzlar. */
import { useEffect, useState } from 'react';
import { BYID, gapOk, shuffle } from '../../data';
import type { Word } from '../../data/types';
import { S, hardWords, started, st, useStore } from '../../store';
import { nav, type GameKey } from '../../lib/nav';
import { toast } from '../../lib/toast';
import { t, tn } from '../../i18n';
import { Ic } from '../../ui/Icon';
import { startCards, startGame, startMatch, startPractice } from '../lesson/engine';
import { GAME_NAMES } from './Games';

type Cnt = 10 | 20 | 30 | 'all';
interface PickState { mode: 'rand' | 'pick'; src: 'auto' | 'hard'; cnt: Cnt; q: string; topic: string }
/** Demo `PK` — sahifa yashar ekan saqlanadi. */
const PK: PickState = { mode: 'rand', src: 'auto', cnt: 10, q: '', topic: 'all' };
const EMPTY: number[] = [];

export function launchGame(k: GameKey, ws: Word[]) {
  if (k === 'cards') startCards(shuffle(ws));
  if (k === 'match') startMatch(ws);
  if (k === 'test') startPractice(shuffle(ws).slice(0, 30), 'Test', true);
  if (k === 'listen') startGame(shuffle(ws).map((w) => ({ t: 'write' as const, w, listen: true })), 'Eshit va yoz');
  if (k === 'gap') {
    const g = ws.filter(gapOk);
    if (!g.length) return toast(t('Bu soʻzlar uchun misol gap yoʻq'));
    startGame(shuffle(g).map((w) => ({ t: 'gap' as const, w })), 'Boʻsh joy');
  }
  if (k === 't60' || k === 'meteor') nav.replace({ k: 'arcade', game: k, pool: ws });
  else if (k === 'snake') nav.replace({ k: 'snake', pool: ws });
}

export function Picker({ game }: { game: GameKey }) {
  const [pk, setPk] = useState<PickState>({ ...PK });
  const upd = (p: Partial<PickState>) => { Object.assign(PK, p); setPk({ ...PK }); };
  useStore((s) => s.w); useStore((s) => s.hm); // started()/hardWords() uchun obuna
  const selRaw = useStore((s) => s.sel);
  const sel = (selRaw ?? EMPTY).filter((id) => st(id)); // faqat oʻrganilgan soʻzlar tanlanadi
  const setSel = (ids: number[]) => S().set({ sel: ids });
  useEffect(() => { if ((selRaw ?? EMPTY).length !== sel.length) setSel(sel); }); // eslint-disable-line

  const hw = hardWords(), stn = started();
  const SRC: Array<[PickState['src'], string, Word[] | null]> = [
    ['auto', tn('Oʻrganilganlar · #', stn.length), stn.length >= 4 ? stn : null],
    ['hard', tn('Qiyin soʻzlar · #', hw.length), hw.length >= 4 ? hw : null],
  ];
  const src: PickState['src'] = pk.src === 'hard' && hw.length >= 4 ? 'hard' : 'auto';
  const topics = ['all', ...new Set(stn.map((w) => w.topic))];
  const topic = topics.includes(pk.topic) ? pk.topic : 'all';
  const q = pk.q.toLowerCase();
  const list = stn.filter((w) => (topic === 'all' || w.topic === topic) && (!q || w.w.toLowerCase().includes(q) || w.uz.toLowerCase().includes(q)));
  const allOn = list.length > 0 && list.every((w) => sel.includes(w.id));
  const srcPool = (SRC.find((x) => x[0] === src) || SRC[0])[2] || [];
  const randN = pk.cnt === 'all' ? srcPool.length : Math.min(pk.cnt, srcPool.length);
  const ready = pk.mode === 'rand' ? randN >= 4 : sel.length >= 4;

  const chip = (key: string, on: boolean, label: string, onClick: () => void, dis = false) => (
    <button key={key} className="f" aria-pressed={on} disabled={dis} onClick={onClick}>{label}</button>
  );
  const toggle = (id: number, on: boolean) => { if (on) { if (!sel.includes(id)) setSel([...sel, id]); } else setSel(sel.filter((x) => x !== id)); };
  const toggleAll = () => { if (allOn) setSel(sel.filter((id) => !list.some((w) => w.id === id))); else setSel([...sel, ...list.filter((w) => !sel.includes(w.id)).map((w) => w.id)]); };
  const start = () => { const ws = pk.mode === 'rand' ? shuffle(srcPool).slice(0, randN) : sel.map((id) => BYID[id]).filter((w): w is Word => !!w); launchGame(game, ws); };

  return (
    <>
      <div className="sbar">
        <button className="x" aria-label={t('Orqaga')} onClick={() => nav.home('games')}><Ic n="back" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t(GAME_NAMES[game])}</b>
      </div>
      <div className="sbody fade" style={{ gap: 16 }}>
        <div className="col g4"><h1 className="h1" style={{ fontSize: 22 }}>{t('Qaysi soʻzlar bilan oʻynaysiz?')}</h1></div>
        <div className="seg">
          <button aria-pressed={pk.mode === 'rand'} onClick={() => upd({ mode: 'rand' })}>{t('Tasodifiy')}</button>
          <button aria-pressed={pk.mode === 'pick'} onClick={() => upd({ mode: 'pick' })}>{t('Oʻzim tanlayman')}</button>
        </div>
        {pk.mode === 'rand' ? (
          <>
            <div className="col g8"><span className="cap">{t('Qayerdan')}</span>
              <div className="chips">{SRC.map(([v, l, p]) => chip(v, src === v, l, () => upd({ src: v }), !p))}</div></div>
            <div className="col g8"><span className="cap">{t('Nechta soʻz')}</span>
              <div className="chips">{([10, 20, 30, 'all'] as Cnt[]).map((v) => chip(String(v), pk.cnt === v, v === 'all' ? t('Hammasi') : String(v), () => upd({ cnt: v })))}</div></div>
            <div className="note" style={{ background: 'var(--primary-soft)', color: 'var(--primary-ink)' }}><Ic n="zap" />
              <span>{randN >= 4 ? tn('Oʻrganilgan soʻzlaringizdan # tasi tasodifiy olinadi. Har safar boshqacha toʻplam chiqadi.', randN) : t('Oʻynash uchun kamida 4 ta oʻrganilgan soʻz kerak. Avval Bosh sahifada mashgʻulot oʻting.')}</span></div>
          </>
        ) : (
          <>
            <label className="search"><Ic n="search" size={20} />
              <input type="search" placeholder={t('Soʻz qidirish')} value={pk.q} onChange={(e) => upd({ q: e.target.value })} /></label>
            <div className="chips scroll">{topics.map((tp) => chip(tp, topic === tp, tp === 'all' ? t('Hammasi') : t(tp), () => upd({ topic: tp })))}</div>
            <div className="between">
              <span className="t13" style={{ fontWeight: 700 }}>{tn('# ta tanlandi', sel.length)}</span>
              <span className="row g12">
                <button className="t13" style={{ color: 'var(--primary-ink)', fontWeight: 800 }} onClick={toggleAll}>{allOn ? t('Belgini olish') : t('Hammasini belgilash')}</button>
                {sel.length ? <button className="t13" style={{ color: 'var(--error-ink)', fontWeight: 800 }} onClick={() => setSel([])}>{t('Tozalash')}</button> : null}
              </span>
            </div>
            {!stn.length ? (
              <div className="card col g8" style={{ padding: 20, alignItems: 'center', textAlign: 'center' }}>
                <span className="ibox" style={{ background: 'var(--primary-soft)', color: 'var(--primary-ink)' }}><Ic n="book" /></span>
                <b className="t15b">{t('Hali oʻrganilgan soʻz yoʻq')}</b>
                <span className="t13">{t('Avval Bosh sahifada mashgʻulot oʻting — oʻrgangan soʻzlaringiz shu yerda chiqadi. Hozircha “Tasodifiy” rejimidan foydalaning.')}</span>
              </div>
            ) : (
              <>
                <p className="t12" style={{ margin: 0 }}>{tn('Faqat oʻrganilgan soʻzlar (# ta) koʻrsatiladi.', stn.length)}</p>
                <div className="wl">
                  {list.length ? list.map((w) => (
                    <label className="wr pick" key={w.id}>
                      <input type="checkbox" checked={sel.includes(w.id)} onChange={(e) => toggle(w.id, e.target.checked)} />
                      <span className="box"><Ic n="check" /></span>
                      <div className="grow"><b>{w.w}</b><span>{w.uz}</span></div>
                      <span className="t12">{t(w.topic)}</span>
                    </label>
                  )) : <p className="t13">{t('Hech narsa topilmadi.')}</p>}
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className="sfoot">
        <button className="btn" disabled={!ready} onClick={start}>
          {ready ? t('Oʻyinni boshlash') : pk.mode === 'rand' ? t('Avval soʻz oʻrganing') : t('Kamida 4 ta soʻz tanlang')}
        </button>
      </div>
    </>
  );
}
